import React, { useState } from "react";
import { useNavigate } from "react-router";
import { Tooltip } from "react-tooltip";
import { CalendarDaysIcon } from "@heroicons/react/20/solid";
import { ChatBubbleBottomCenterTextIcon } from "@heroicons/react/24/solid";
import { StarIcon } from "@heroicons/react/24/outline";
import { timeAgo } from "../../app/datetimeUtils";
import { getActionUrl, getStepName } from "../../app/draftHelper";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { handleEnterSpaceKey, handleSpaceKey } from "../../app/keyboardUtils";
import { CourseState, setCourse, setCourses } from "../../app/userSlice";
import DescriptiveDate from "../../components/DescriptiveDate";
import FormattedDate from "../../components/FormattedDate";
import TextWithTip from "../../components/TextWithTip";
import {
	useChangeFavoriteMutation,
	useMakeCourseSelectionMutation,
} from "../courses/coursesApiSlice";
import {
	ActiveDashboardCardViewModel,
	DashboardEssayViewModel,
	DashboardLessonViewModel,
	DashboardPeerReviewViewModel,
	DashboardViewModel,
	InactiveDashboardCardViewModel,
} from "../dashboard/dashboardApiSlice";
import { useGetGradesQuery } from "../lookup/lookupApiSlice";

export interface Props {
	cards: DashboardViewModel;
	showAll: boolean;
}

export default function StudentDashboard(props: Props) {
	function initialIsFavorites(): boolean[] {
		return props.cards.activeCourseCards.map((c) => c.isFavorite);
	}

	const [favorites, setFavorites] = useState<boolean[]>(initialIsFavorites());
	const [changeFavorite, changeFavoriteResult] = useChangeFavoriteMutation();
	const [chooseCourse, chooseCourseResult] = useMakeCourseSelectionMutation();

	const gradesResult = useGetGradesQuery();
	const grades = gradesResult.data;

	const dispatch = useAppDispatch();
	const navigate = useNavigate();

	const { courses } = useAppSelector((state) => state.user);

	function handleChooseInactiveCourse(id: number, name: string, gradeId: number) {
		if (!grades) return;
		chooseCourse(id)
			.unwrap()
			.then((showAvatars) => {
				dispatch(
					setCourse({
						id,
						name,
						gradeId,
						isActive: false,
						showUserAvatars: showAvatars,
					})
				);
				navigate("/reports");
			})
			.catch((error) => console.log(error));
	}

	function handleChooseCourse(
		id: number,
		name: string,
		gradeId: number,
		path: string,
		section?: string
	) {
		if (!grades) return;
		chooseCourse(id)
			.unwrap()
			.then((showAvatars) => {
				dispatch(
					setCourse({
						id,
						name,
						gradeId,
						isActive: true,
						showUserAvatars: showAvatars,
					})
				);
				const options = section ? { state: { section: section } } : undefined;
				navigate(path, options);
			})
			.catch((error) => console.log(error));
	}

	function handleSublink(
		courseId: number,
		courseName: string,
		gradeId: number,
		sublink: string,
		promptId?: number,
		scrollToMessage?: boolean
	) {
		const options =
			promptId !== undefined || scrollToMessage === true
				? {
						state: {
							promptId,
							section: scrollToMessage ? "EssayMessages" : undefined,
						},
					}
				: undefined;

		chooseCourse(courseId)
			.unwrap()
			.then((showAvatars) => {
				dispatch(
					setCourse({
						id: courseId,
						name: courseName,
						gradeId,
						isActive: true,
						showUserAvatars: showAvatars,
					})
				);
				navigate(sublink, options);
			})
			.catch((error) => console.log(error));
	}

	const handleEssayClick = (
		essay: DashboardEssayViewModel,
		courseId: number,
		courseName: string,
		gradeId: number,
		scrollToMessage?: boolean
	) => {
		if (!essay) {
			console.log(`handleEssayClick: row lookup failed for essayId`);
			return;
		}

		const actionUrl = getActionUrl(essay.action, essay.actionDraftId, essay.promptId);

		if (!actionUrl) {
			console.log(`handleEssayClick: unknown action "${essay.action}"`);
			return;
		}

		handleSublink(courseId, courseName, gradeId, actionUrl, essay.promptId, scrollToMessage);
	};

	const handleChangeFavorite = (
		event: React.UIEvent,
		index: number,
		course: ActiveDashboardCardViewModel
	) => {
		event.stopPropagation();

		const favoritesValues = [...favorites];
		favoritesValues[index] = !favoritesValues[index];
		setFavorites(favoritesValues);

		changeFavorite({ userCourseId: course.userCourseId, favoriteState: favoritesValues[index] })
			.unwrap()
			.then(() => {
				if (courses) {
					const cs = courses.map((c) => {
						if (c.id === course.courseId) {
							return { ...c, isFavorite: favoritesValues[index] };
						}
						return c;
					});
					dispatch(setCourses(cs));
				}
			})
			.catch((error) => {
				console.log(error);
			});
	};

	return (
		<div>
			<div className="bg-white">
				{props.showAll ? (
					<div>
						<div className="mx-auto max-w-7xl">
							<div className="isolate mx-auto mt-10 grid max-w-md grid-cols-1 gap-8 lg:mx-0 lg:max-w-none lg:grid-cols-3 course-card-container">
								{props.cards.inactiveCourseCards.map(
									(course: InactiveDashboardCardViewModel, index: number) => (
										<InactiveCard
											key={course.courseId}
											course={course}
											index={index}
											handleChooseInactiveCourse={handleChooseInactiveCourse}
										/>
									)
								)}
							</div>
						</div>
					</div>
				) : (
					<div className="mx-auto max-w-7xl">
						<div className="isolate mx-auto mt-10 grid max-w-md grid-cols-1 gap-8 lg:mx-0 lg:max-w-none lg:grid-cols-3 course-card-container">
							{props.cards.activeCourseCards.map(
								(course: ActiveDashboardCardViewModel, index: number) => {
									return (
										<ActiveCard
											key={course.courseId}
											course={course}
											index={index}
											courses={courses}
											handleChangeFavorite={handleChangeFavorite}
											handleChooseCourse={handleChooseCourse}
											handleEssayClick={handleEssayClick}
											handleSublink={handleSublink}
										/>
									);
								}
							)}
						</div>
					</div>
				)}
			</div>
		</div>
	);
}

interface InactiveCardProps {
	course: InactiveDashboardCardViewModel;
	index: number;
	handleChooseInactiveCourse: (id: number, name: string, gradeId: number) => void;
}

function InactiveCard({
	course,
	index,
	handleChooseInactiveCourse: handleInactiveChooseCourse,
}: InactiveCardProps) {
	return (
		<div
			id={`inactive-course-card-${course.courseId}`}
			key={course.courseId}
			className="ring-2 ring-primary rounded-3xl">
			<section
				id={`inactive-card-header-${course.courseId}`}
				className="relative cursor-pointer bg-slate-100 py-4 px-8 rounded-t-3xl border border-b-slate-200 h-[7.5rem] hover:bg-slate-200"
				onClick={() =>
					handleInactiveChooseCourse(course.courseId, course.courseName, course.gradeId)
				}>
				<h3 className="line-clamp-2 text-primary-700 text-xl font-semibold leading-6 mb-4 pr-4">
					<TextWithTip uniqueName={`course${course.courseId}`} text={course.courseName} />
				</h3>
				<h4 className=" line-clamp-1 text-primary-700 text-lg font-semibold">
					<TextWithTip
						uniqueName={`teacher${course.courseId}`}
						text={course.teachers.join(", ")}
					/>
				</h4>
			</section>
			<section className="pt-10 pb-4 px-8 cursor-default">
				<p className="flex items-center justify-center text-m ">
					<DescriptiveDate
						date={course.createDate}
						action={"Created"}
						preposition={"on"}
					/>
				</p>
			</section>
			<section className="py-6 px-8">
				<a
					tabIndex={0}
					id={`view-reports-${course.courseId}`}
					onClick={() =>
						handleInactiveChooseCourse(
							course.courseId,
							course.courseName,
							course.gradeId
						)
					}
					className="font-button bg-primary-700 text-white cursor-pointer no-underline shadow-sm hover:bg-primary-800 block rounded-full py-2 px-3 text-center text-sm font-semibold leading-6 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-700">
					View Reports
				</a>
			</section>
		</div>
	);
}

interface ActiveCardProps {
	course: ActiveDashboardCardViewModel;
	index: number;
	courses: CourseState[] | null;
	handleChangeFavorite: (
		event: React.UIEvent,
		index: number,
		course: ActiveDashboardCardViewModel
	) => void;
	handleChooseCourse: (
		id: number,
		name: string,
		gradeId: number,
		path: string,
		section?: string
	) => void;
	handleEssayClick: (
		essay: DashboardEssayViewModel,
		id: number,
		name: string,
		gradeId: number
	) => void;
	handleSublink: (
		id: number,
		name: string,
		gradeId: number,
		sublink: string,
		promptId?: number
	) => void;
}

function ActiveCard({
	course,
	index,
	courses,
	handleChangeFavorite,
	handleChooseCourse,
	handleEssayClick,
	handleSublink,
}: ActiveCardProps) {
	return (
		<div
			id={`course-card-${course.courseId}`}
			key={course.courseId}
			className="bg-white ring-2 ring-primary rounded-3xl flex flex-col justify-between">
			<div className="bg-slate-100 py-4 px-8 rounded-t-3xl border-b border-b-slate-200 hover:bg-slate-200">
				<ActiveCardHeader
					course={course}
					index={index}
					courses={courses}
					handleChangeFavorite={handleChangeFavorite}
					handleChooseCourse={handleChooseCourse}
				/>
			</div>
			<div className="px-8">
				<WritingSection
					course={course}
					handleChooseCourse={handleChooseCourse}
					handleEssayClick={handleEssayClick}
					handleSublink={handleSublink}
				/>

				<LessonsSection
					course={course}
					handleChooseCourse={handleChooseCourse}
					handleSublink={handleSublink}
				/>

				<PeerReviewSection
					course={course}
					handleChooseCourse={handleChooseCourse}
					handleSublink={handleSublink}
				/>
			</div>
			<div className="px-8 pb-6">
				<button
					id={`open-course-${course.courseId}`}
					onClick={() =>
						handleChooseCourse(
							course.courseId,
							course.courseName,
							course.gradeId,
							"/home"
						)
					}
					className="font-button bg-primary-700 text-white no-underline shadow-sm hover:bg-primary-800 focus:bg-primary-800 cursor-pointer mt-6 block rounded-full w-full py-2 px-3 text-center text-sm font-semibold leading-6 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-700">
					Open Course
				</button>
			</div>
		</div>
	);
}

interface CardHeaderProps {
	course: ActiveDashboardCardViewModel;
	index: number;
	courses: CourseState[] | null;

	handleChangeFavorite: (
		event: React.UIEvent,
		index: number,
		course: ActiveDashboardCardViewModel
	) => void;
	handleChooseCourse: (
		id: number,
		name: string,
		gradeId: number,
		path: string,
		section?: string
	) => void;
}

function ActiveCardHeader(props: CardHeaderProps) {
	const { course, index, courses } = props;
	const isFavorite = courses?.find((c) => c.id === course.courseId)?.isFavorite ?? false;

	const handleChangeFavorite = (event: React.UIEvent) => {
		props.handleChangeFavorite(event, index, course);
	};

	return (
		<section
			id={`card-header-${course.courseId}`}
			className="relative cursor-pointer"
			onClick={() =>
				props.handleChooseCourse(
					course.courseId,
					course.courseName,
					course.gradeId,
					"/home"
				)
			}>
			<button
				id={`favorite-${course.courseId}`}
				className="h-10 w-10 shrink-0 absolute -top-2 -right-6 stroke-1"
				onClick={(event) => handleChangeFavorite(event)}
				onKeyDown={(event) => handleSpaceKey(event, handleChangeFavorite)}>
				<StarIcon
					className={`${
						isFavorite
							? "fill-yellow-300 hover:fill-yellow-400 focus:fill-yellow-400"
							: "fill-white hover:fill-yellow-100 focus:fill-yellow-100"
					}`}
				/>
			</button>
			<h3 className="line-clamp-2 text-primary-700 text-xl font-semibold leading-6 h-12 mb-4 pr-4">
				<TextWithTip uniqueName={`course${course.courseId}`} text={course.courseName} />
			</h3>
			<h4 className="line-clamp-1 text-primary-700 text-lg font-semibold">
				<TextWithTip
					uniqueName={`teacher${course.courseId}`}
					text={course.teachers.join(", ")}
				/>
			</h4>
		</section>
	);
}
interface SectionProps {
	course: ActiveDashboardCardViewModel;
	handleChooseCourse: (
		id: number,
		name: string,
		gradeId: number,
		path: string,
		section?: string
	) => void;
	handleSublink: (
		id: number,
		name: string,
		gradeId: number,
		sublink: string,
		promptId?: number
	) => void;
}

interface WritingSectionProps extends SectionProps {
	handleEssayClick: (
		essay: DashboardEssayViewModel,
		id: number,
		name: string,
		gradeId: number,
		scrollToMessage?: boolean
	) => void;
}

function WritingSection({ course, handleChooseCourse, handleEssayClick }: WritingSectionProps) {
	return (
		<div className="cursor-default">
			<div className="mt-4 flex items-center justify-between gap-x-4">
				<h4 className="text-gray-600 font-bold">Writing</h4>
				<button
					id={`view-all-essays-${course.courseId}`}
					onClick={() =>
						handleChooseCourse(
							course.courseId,
							course.courseName,
							course.gradeId,
							"/writing"
						)
					}
					className="cursor-pointer text-primary no-underline py-1 px-2.5 text-xs font-semibold leading-5 hover:underline focus:underline">
					View All
				</button>
			</div>
			<div className="h-44">
				{course.topEssays.length == 0 ? (
					<div className="flex flex-col items-center pt-6">
						<p>Start practicing your writing now!</p>
						<button
							id={`lets-go-button-${course.courseId}`}
							className="mx-auto mt-4 cursor-pointer bg-primary-700 text-white no-underline shadow-sm hover:bg-primary-800 focus:bg-primary-800 rounded-full py-2 px-4 text-center text-sm font-semibold leading-6 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-700"
							onClick={() =>
								handleChooseCourse(
									course.courseId,
									course.courseName,
									course.gradeId,
									"/writing"
								)
							}>
							Let’s Go!
						</button>
					</div>
				) : (
					<table className="w-full table-fixed">
						<tbody>
							{course.topEssays.map((essay: DashboardEssayViewModel) => (
								<WritingRow
									key={`${essay.essayId}-${essay.promptId}`}
									course={course}
									essay={essay}
									handleEssayClick={handleEssayClick}
								/>
							))}
						</tbody>
					</table>
				)}
			</div>
		</div>
	);
}

interface WritingRowProps {
	course: ActiveDashboardCardViewModel;
	essay: DashboardEssayViewModel;
	handleEssayClick: (
		essay: DashboardEssayViewModel,
		id: number,
		name: string,
		gradeId: number,
		scrollToMessage?: boolean
	) => void;
}

function WritingRow(props: WritingRowProps) {
	const { course, essay } = props;
	const step = getStepName(essay.step, essay.action);

	const handleEssayClick = () => {
		props.handleEssayClick(essay, course.courseId, course.courseName, course.gradeId);
	};
	const handleEssayScrollToClick = () => {
		props.handleEssayClick(essay, course.courseId, course.courseName, course.gradeId, true);
	};
	return (
		<tr
			tabIndex={0}
			id={`essay-${course.courseId}-${essay.promptId}-${essay.essayId}`}
			className="cursor-pointer hover:bg-slate-50 focus:bg-slate-50 border-y-[1px] border-y-gray-200 first:border-t-transparent last:border-b-transparent hover:first:border-t-gray-200 hover:last:hover:border-b-gray-200"
			onClick={handleEssayClick}
			onKeyDown={(e) => handleEnterSpaceKey(e, handleEssayClick)}>
			<td className="w-1/12">
				{essay.hasDueDate || essay.isAssigned ? (
					<div className="mx-2 group relative text-center">
						<CalendarDaysIcon
							tabIndex={-1}
							data-tooltip-id={`essay-tooltip-${essay.essayId}`}
							className="h-5 w-5"
						/>
						<Tooltip
							id={`essay-tooltip-${essay.essayId}`}
							place="top-start"
							content={`Assigned to ${essay.isAssigned ? "you" : "your class"}`}
						/>
					</div>
				) : null}
			</td>
			<td className="w-11/12 pl-4 pr-3 py-1 text-sm font-medium text-gray-900">
				<p className="truncate font-bold text-gray-700">{essay.promptTitle}</p>
				<div className="flex justify-between">
					<div>
						{essay.dueDate != null ? (
							<p className="text-xs pl-2 uppercase">Due {timeAgo(essay.dueDate)}</p>
						) : (
							<p className="text-xs pl-2 uppercase">No due date</p>
						)}
						<p className="text-xs pl-2">{step}</p>
					</div>
					<div className="w-1/12">
						{essay.newMessageCount > 0 ? (
							<div
								className="pl-2 sm:pl-0 group cursor-pointer relative text-center"
								onClick={handleEssayScrollToClick}
								onKeyDown={(e) => handleEnterSpaceKey(e, handleEssayScrollToClick)}>
								<ChatBubbleBottomCenterTextIcon
									data-tooltip-id={`messages-tooltip-${essay.essayId}`}
									className="h-5 w-5"
								/>
								<Tooltip
									id={`messages-tooltip-${essay.essayId}`}
									place="top-start"
									content={`${essay.newMessageCount} new ${
										essay.newMessageCount === 1 ? "message" : "messages"
									}`}
								/>
							</div>
						) : null}
					</div>
				</div>
			</td>
		</tr>
	);
}

function LessonsSection(props: SectionProps) {
	const { course } = props;
	return (
		<div className="cursor-default">
			<div className="mt-4 flex items-center justify-between gap-x-4">
				<h4 className="text-gray-600 font-bold">Lessons</h4>
				<button
					id={`view-all-lessons-${course.courseId}`}
					onClick={() =>
						props.handleChooseCourse(
							course.courseId,
							course.courseName,
							course.gradeId,
							"/lessons"
						)
					}
					className="hover:cursor-pointer text-primary no-underline py-1 px-2.5 text-xs font-semibold leading-5 hover:underline focus:underline">
					View All
				</button>
			</div>
			<div className="h-32">
				{course.topLessons.length == 0 ? (
					<div className="flex items-center justify-center text-center mx-4 pt-2">
						<p>You don’t have any lessons assigned to you!</p>
					</div>
				) : (
					<table className="w-full table-fixed">
						<tbody>
							{course.topLessons.map((lesson: DashboardLessonViewModel) => (
								<LessonRow
									key={lesson.lessonId}
									course={course}
									lesson={lesson}
									handleSublink={props.handleSublink}
								/>
							))}
						</tbody>
					</table>
				)}
			</div>
		</div>
	);
}

interface LessonRowProps {
	course: ActiveDashboardCardViewModel;
	lesson: DashboardLessonViewModel;
	handleSublink: (
		id: number,
		name: string,
		gradeId: number,
		sublink: string,
		promptId?: number
	) => void;
}

function LessonRow(props: LessonRowProps) {
	const { course, lesson } = props;
	const handleSublinkClick = () => {
		props.handleSublink(
			course.courseId,
			course.courseName,
			course.gradeId,
			`/lessons/${lesson.codeName}`
		);
	};
	return (
		<tr
			tabIndex={0}
			id={`lesson-${lesson.lessonId}`}
			className="cursor-pointer hover:bg-slate-50 focus:bg-slate-50 border-y-[1px] border-y-gray-200 first:border-t-transparent last:border-b-transparent hover:first:border-t-gray-200 hover:last:hover:border-b-gray-200"
			onClick={handleSublinkClick}
			onKeyDown={(e) => handleEnterSpaceKey(e, handleSublinkClick)}>
			<td className="w-1/12">
				{lesson.isAssignment ? (
					<div className="mx-2 group relative text-center">
						{
							<CalendarDaysIcon
								tabIndex={-1}
								data-tooltip-id="lesson-tooltip"
								className="h-5 w-5"
							/>
						}
						<Tooltip
							id="lesson-tooltip"
							place="top-start"
							content="Assigned By Teacher"
						/>
					</div>
				) : null}
			</td>
			<td className="w-11/12 pl-4 pr-3 py-1 text-sm font-medium text-gray-900">
				<p className="truncate font-bold text-gray-700">{lesson.lessonTitle}</p>
				{lesson.isStarted ? (
					<p className="text-xs pl-2">In Progress: {lesson.timeSpent} min spent</p>
				) : (
					<p className="text-xs pl-2">Not Started Yet</p>
				)}
			</td>
		</tr>
	);
}

function PeerReviewSection({ course, handleChooseCourse, handleSublink }: SectionProps) {
	return (
		<div className="cursor-default">
			<div className="mt-4 flex items-center justify-between gap-x-4">
				<h4 className="text-gray-600 font-bold">Peer Reviews</h4>
				<button
					id={`view-all-reviews-${course.courseId}`}
					onClick={() =>
						handleChooseCourse(
							course.courseId,
							course.courseName,
							course.gradeId,
							"/reviews"
						)
					}
					className="hover:cursor-pointer text-primary no-underline py-1 px-2.5 text-xs font-semibold leading-5 hover:underline focus:underline">
					View All
				</button>
			</div>
			<div className="h-20">
				{course.topPeerReviews.length == 0 ? (
					<div className="flex items-center justify-center text-center mx-4 pt-2">
						<p>You don’t have any peer reviews to do!</p>
					</div>
				) : (
					<table className="w-full table-fixed">
						<tbody>
							{course.topPeerReviews.map(
								(peerReview: DashboardPeerReviewViewModel) => (
									<PeerReviewRow
										key={peerReview.reviewId}
										course={course}
										peerReview={peerReview}
										handleSublink={handleSublink}
									/>
								)
							)}
						</tbody>
					</table>
				)}
			</div>
		</div>
	);
}

interface PeerReviewRowProps {
	course: ActiveDashboardCardViewModel;
	peerReview: DashboardPeerReviewViewModel;
	handleSublink: (
		id: number,
		name: string,
		gradeId: number,
		sublink: string,
		promptId?: number
	) => void;
}

function PeerReviewRow(props: PeerReviewRowProps) {
	const { course, peerReview } = props;
	const handleSublinkClick = () => {
		props.handleSublink(
			course.courseId,
			course.courseName,
			course.gradeId,
			`/reviews/${peerReview.reviewId}`
		);
	};

	return (
		<tr
			tabIndex={0}
			id={`review-${peerReview.reviewId}`}
			className="cursor-pointer hover:bg-slate-50 focus:bg-slate-50 border-y-[1px] border-y-gray-200 first:border-t-transparent last:border-b-transparent hover:first:border-t-gray-200 hover:last:hover:border-b-gray-200"
			onClick={handleSublinkClick}
			onKeyDown={(e) => handleEnterSpaceKey(e, handleSublinkClick)}>
			<td className="w-1/12">
				<div className="mx-2 group relative text-center">
					{
						<CalendarDaysIcon
							tabIndex={-1}
							data-tooltip-id="peerReview-tooltip"
							className="h-5 w-5"
						/>
					}
					<Tooltip id="peerReview-tooltip" place="top-start">
						<p>
							Requested on <FormattedDate date={peerReview.requestDate} />
						</p>
					</Tooltip>
				</div>
			</td>
			<td className="w-11/12 pl-4 pr-3 py-1 text-sm font-medium text-gray-900">
				<p className="truncate font-bold text-gray-700">{peerReview.promptTitle}</p>
				<p className="text-xs pl-2">Requested by {peerReview.author}</p>
			</td>
		</tr>
	);
}
